import { useEffect, useRef, useState } from "react";
import SEO from "../components/SEO";
import { EvoluraLogo } from "../components/EvoluraLogo";
import { Button } from "../components/ui/button";

const CTA_URL = "https://www.evoluras.com";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_dev-insights-15/artifacts/fzrj6i2l_3OmY2ljazByPfHse.png";

// Kick off Google OAuth (same flow as main landing).
const loginHref = () => {
  const inviteToken = typeof window !== "undefined" ? (sessionStorage.getItem("pending_invite_token") || "") : "";
  const returnUrl = typeof window !== "undefined" ? (sessionStorage.getItem("pending_return_url") || "") : "";
  const params = new URLSearchParams();
  if (inviteToken) params.set("invite_token", inviteToken);
  if (returnUrl) params.set("return_url", returnUrl);
  const qs = params.toString();
  return `/api/auth/google/login${qs ? `?${qs}` : ""}`;
};

// Tool route mapping (guest mode — no signup needed)
const TOOL_ROUTES = {
  "Personal SWOT": "/swot",
  "Core Values": "/values",
  "Wheel of Life": "/wheel",
};

// Stable pravatar seeds (already whitelisted by backend image proxy)
const AVATARS = {
  ana: "https://i.pravatar.cc/120?img=47",    // warmer, older — mentor
  mum: "https://i.pravatar.cc/120?img=49",    // maternal
  sarah: "https://i.pravatar.cc/120?img=32",  // younger friend
};

// ---- Static content ----
const SIGNS = [
  "You say yes out of kindness, then feel a small resentment you can't quite name.",
  "You've built a life that looks right from the outside and feels borrowed from the inside.",
  "You know you're gifted at reading what others need. You're less sure what you need.",
  "You soften your opinions. You round your edges. You call it being reasonable.",
  "Your closest friends describe you in ways that sound truer than how you describe yourself.",
  "There's a version of your life you've been quietly postponing. You know what it is.",
];

const TOOLS = [
  {
    numeral: "I",
    subtitle: "Self-portrait",
    name: "Personal SWOT",
    description:
      "See yourself the way a loving but honest biographer would. What's actually working. What you keep excusing. What becomes possible if you stop.",
  },
  {
    numeral: "II",
    subtitle: "Ground truth",
    name: "Core Values",
    description:
      "Not the values that sound good on a wedding speech. The ones you'd feel sick betraying. Fifty values, fifty anti-values, one ruthless pyramid.",
  },
  {
    numeral: "III",
    subtitle: "Attention map",
    name: "Wheel of Life",
    description:
      "Rate every area of your life, honestly, and watch the shape of your attention become impossible to unsee. Then choose where to begin.",
  },
];

const FAQS = [
  {
    q: "Is any of this actually free?",
    a: "Yes. Personal SWOT, Core Values, and Wheel of Life are free forever. You can do them as a guest without signing up. An account only exists so you can save your work and, when you're ready, invite the people you trust.",
  },
  {
    q: "I don't want anyone to know I'm doing this.",
    a: "Everything you do is private by default. No one — not even someone you later invite — sees anything unless you actively share it with them. You're always the editor of your own story.",
  },
  {
    q: "What if I don't have people I trust enough to ask?",
    a: "Start with the self-reflection tools alone. They work on their own. And often what emerges there is exactly the clarity you need to recognise who in your life has been offering honest mirrors all along.",
  },
  {
    q: "Is this therapy? Coaching?",
    a: "It's neither, and it doesn't replace either. Evoluras is a set of structured self-reflection tools with the option to include trusted people. Think of it as the scaffolding you can bring to your own inner work — or to your next therapy session.",
  },
  {
    q: "Why is this framed for women?",
    a: "Everyone is welcome on Evoluras. This page exists because the pattern of performing a self to be acceptable, then losing track of the real one, is something a lot of women have named specifically and need language for. If it speaks to you, whoever you are, you belong here.",
  },
  {
    q: "How long does the first tool take?",
    a: "The Wheel of Life takes about 5 minutes. Core Values takes 10–15. Personal SWOT takes 15–20. You can stop and come back. Nothing about this is meant to be rushed.",
  },
];

// ---- Small pieces ----
const Eyebrow = ({ children }) => (
  <div className="w-eyebrow" style={{
    fontSize: 11, letterSpacing: "0.3em", textTransform: "uppercase",
    color: "var(--w-rose)", fontFamily: "var(--w-body)", fontWeight: 400,
    display: "inline-flex", alignItems: "center", gap: 12,
  }}>
    <span style={{ width: 28, height: 1, background: "var(--w-rose)", opacity: 0.6 }} />
    {children}
  </div>
);

const Reveal = ({ children, delay = 0, as: Tag = "div", style = {}, className = "" }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      });
    }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <Tag
      ref={ref}
      className={className}
      style={{
        ...style,
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(24px)",
        transition: `opacity 0.9s cubic-bezier(0.22,1,0.36,1) ${delay}ms, transform 0.9s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
      }}
    >
      {children}
    </Tag>
  );
};

// ---- Mirror / feedback card ----
const MirrorCard = ({ label, quote, tilt = 0, className = "", floating = false, avatarSrc, avatarInitial, avatarHue = 270 }) => (
  <div
    className={className}
    style={{
      background: "rgba(26, 21, 51, 0.72)",
      backdropFilter: "blur(14px)",
      WebkitBackdropFilter: "blur(14px)",
      border: "0.5px solid rgba(168, 156, 255, 0.22)",
      borderRadius: 14,
      padding: "18px 20px",
      transform: `rotate(${tilt}deg)`,
      boxShadow: floating ? "0 20px 60px -20px rgba(0,0,0,0.5)" : "none",
      maxWidth: 280,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
      {avatarSrc ? (
        <img
          src={avatarSrc}
          alt=""
          loading="lazy"
          style={{
            width: 32, height: 32, borderRadius: "50%",
            objectFit: "cover", flexShrink: 0,
            border: "0.5px solid rgba(168,156,255,0.3)",
          }}
        />
      ) : avatarInitial ? (
        <div
          style={{
            width: 28, height: 28, borderRadius: "50%",
            background: `radial-gradient(circle at 30% 30%, hsl(${avatarHue}, 55%, 70%), hsl(${avatarHue + 20}, 40%, 40%))`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "var(--w-display)", fontStyle: "italic", fontWeight: 300,
            color: "#fff", fontSize: 14, flexShrink: 0,
          }}
        >
          {avatarInitial}
        </div>
      ) : null}
      <div style={{
        fontFamily: "var(--w-body)",
        fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase",
        color: "var(--w-muted)", fontWeight: 400,
      }}>
        {label}
      </div>
    </div>
    <p
      style={{
        fontFamily: "var(--w-display)", fontStyle: "italic", fontWeight: 300,
        fontSize: 16, lineHeight: 1.5, color: "var(--w-soft)", margin: 0,
      }}
    >
      {quote}
    </p>
  </div>
);

// ---- FAQ Item ----
const FaqItem = ({ q, a, isOpen, onToggle, index }) => (
  <Reveal delay={index * 60}>
    <div
      style={{
        borderBottom: "0.5px solid var(--w-line)",
        padding: "22px 0",
      }}
    >
      <button
        onClick={onToggle}
        style={{
          width: "100%", background: "transparent", border: "none", color: "var(--w-text)",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          gap: 20, textAlign: "left", cursor: "pointer", padding: 0,
          fontFamily: "var(--w-display)", fontWeight: 300, fontSize: 20, letterSpacing: "-0.01em",
          lineHeight: 1.4,
        }}
      >
        <span>{q}</span>
        <span
          style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 28, height: 28, flexShrink: 0,
            color: "var(--w-accent)", fontSize: 24, fontWeight: 300, fontFamily: "var(--w-body)",
            transition: "transform 0.35s cubic-bezier(0.22,1,0.36,1)",
            transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
          }}
        >
          +
        </span>
      </button>
      <div
        style={{
          maxHeight: isOpen ? 360 : 0,
          overflow: "hidden",
          transition: "max-height 0.5s cubic-bezier(0.22,1,0.36,1), margin 0.5s",
          marginTop: isOpen ? 14 : 0,
        }}
      >
        <p
          style={{
            fontFamily: "var(--w-body)", fontWeight: 300,
            color: "var(--w-soft)", fontSize: 15, lineHeight: 1.7,
            margin: 0, paddingRight: 48, maxWidth: 620,
          }}
        >
          {a}
        </p>
      </div>
    </div>
  </Reveal>
);

// ---- Primary Button ----
const CTAButton = ({ children, href, variant = "primary", testId }) => {
  const resolvedHref = href || loginHref();
  const [hover, setHover] = useState(false);
  if (variant === "ghost") {
    return (
      <a
        href={resolvedHref}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        data-testid={testId}
        style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          color: hover ? "var(--w-text)" : "var(--w-soft)",
          fontFamily: "var(--w-body)", fontWeight: 400, fontSize: 14,
          textDecoration: "none", letterSpacing: "0.02em",
          transition: "color 0.2s",
          borderBottom: hover ? "0.5px solid var(--w-accent)" : "0.5px solid transparent",
          paddingBottom: 2,
        }}
      >
        {children}
      </a>
    );
  }
  return (
    <a
      href={resolvedHref}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid={testId}
      style={{
        display: "inline-flex", alignItems: "center", gap: 10,
        padding: "14px 28px", borderRadius: 100,
        background: hover ? "var(--w-accent)" : "var(--w-text)",
        color: "#0b0818",
        fontFamily: "var(--w-body)", fontWeight: 500, fontSize: 14,
        textDecoration: "none",
        transform: hover ? "translateY(-2px)" : "translateY(0)",
        boxShadow: hover ? "0 12px 28px -8px rgba(168,156,255,0.55)" : "0 1px 0 rgba(0,0,0,0.05)",
        transition: "background 0.3s, transform 0.25s, box-shadow 0.3s",
      }}
    >
      {children}
      <span
        style={{
          display: "inline-block",
          transform: hover ? "translateX(6px)" : "translateX(0)",
          transition: "transform 0.25s",
          fontSize: 16, lineHeight: 1,
        }}
      >
        →
      </span>
    </a>
  );
};

// ---- Tool Card ----
const ToolCard = ({ t, i }) => {
  const [hover, setHover] = useState(false);
  const [arrowHover, setArrowHover] = useState(false);
  return (
    <Reveal delay={i * 120}>
      <div
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          position: "relative",
          background: "var(--w-card)",
          border: `0.5px solid ${hover ? "rgba(168,156,255,0.45)" : "var(--w-line)"}`,
          borderRadius: 18,
          padding: "38px 32px 34px",
          transform: hover ? "translateY(-4px)" : "translateY(0)",
          transition: "transform 0.35s, border-color 0.3s",
          height: "100%",
          display: "flex", flexDirection: "column",
        }}
      >
        {/* Top gradient line on hover */}
        <span
          style={{
            position: "absolute", top: 0, left: 32, right: 32, height: 1,
            background: "linear-gradient(90deg, transparent, var(--w-accent), transparent)",
            opacity: hover ? 1 : 0, transition: "opacity 0.4s",
          }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
          <span style={{
            fontFamily: "var(--w-display)", fontStyle: "italic", fontWeight: 300,
            fontSize: 22, color: "var(--w-accent)",
          }}>
            {t.numeral}
          </span>
          <span style={{
            fontFamily: "var(--w-body)", fontSize: 11, textTransform: "uppercase",
            letterSpacing: "0.2em", color: "var(--w-muted)",
          }}>
            {t.subtitle}
          </span>
          <span style={{ flex: 1, height: 1, background: "var(--w-line)" }} />
        </div>
        <h3 style={{
          fontFamily: "var(--w-display)", fontWeight: 300, fontSize: 30,
          letterSpacing: "-0.02em", color: "var(--w-text)",
          margin: "0 0 18px", lineHeight: 1.15,
        }}>
          {t.name}
        </h3>
        <p style={{
          fontFamily: "var(--w-body)", fontWeight: 300, fontSize: 15,
          lineHeight: 1.65, color: "var(--w-soft)", margin: "0 0 36px", flex: 1,
        }}>
          {t.description}
        </p>
        <a
          href={TOOL_ROUTES[t.name] || loginHref()}
          onMouseEnter={() => setArrowHover(true)}
          onMouseLeave={() => setArrowHover(false)}
          data-testid={`w-tool-${(t.name || "").toLowerCase().replace(/\s+/g, "-")}`}
          style={{
            fontFamily: "var(--w-body)", fontSize: 11, textTransform: "uppercase",
            letterSpacing: arrowHover ? "0.32em" : "0.2em",
            color: "var(--w-accent)", textDecoration: "none", fontWeight: 500,
            transition: "letter-spacing 0.3s",
          }}
        >
          Begin →
        </a>
      </div>
    </Reveal>
  );
};

// ---- Sign Row ----
const SignRow = ({ num, text, i }) => {
  const [hover, setHover] = useState(false);
  return (
    <Reveal delay={i * 70}>
      <div
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          padding: "28px 24px",
          borderBottom: "0.5px solid var(--w-line)",
          background: hover ? "rgba(168,156,255,0.03)" : "transparent",
          transition: "background 0.3s",
          display: "flex", gap: 20,
        }}
      >
        <span style={{
          fontFamily: "var(--w-display)", fontStyle: "italic", fontWeight: 300,
          fontSize: 16, color: "var(--w-accent)", flexShrink: 0,
          paddingTop: 4, letterSpacing: "0.05em",
        }}>
          {num}
        </span>
        <p style={{
          fontFamily: "var(--w-display)", fontWeight: 300, fontSize: 19,
          letterSpacing: "-0.01em", lineHeight: 1.5, color: "var(--w-text)",
          margin: 0,
        }}>
          {text}
        </p>
      </div>
    </Reveal>
  );
};

// ---- Main Page ----
const LandingPageW = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [mounted, setMounted] = useState(false);

  // Load Fraunces + Inter Tight on mount
  useEffect(() => {
    const urls = [
      "https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,300;0,400;1,300;1,400&family=Inter+Tight:wght@300;400;500&display=swap",
    ];
    const links = urls.map((href) => {
      const l = document.createElement("link");
      l.rel = "stylesheet"; l.href = href; document.head.appendChild(l);
      return l;
    });
    // Trigger entrance animation
    const t = setTimeout(() => setMounted(true), 80);
    return () => { links.forEach((l) => l.remove()); clearTimeout(t); };
  }, []);

  const entry = (delay) => ({
    opacity: mounted ? 1 : 0,
    transform: mounted ? "translateY(0)" : "translateY(20px)",
    transition: `opacity 0.9s cubic-bezier(0.22,1,0.36,1) ${delay}ms, transform 0.9s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
  });

  return (
    <>
      <SEO
        title="Evoluras — For the woman done performing."
        description="A quiet place for women who are tired of the version of themselves they built to be loved. Come home to who you actually are."
        path="/w"
        ogImage={HERO_IMAGE}
      />
      <style>{`
        :root {
          --w-bg: #0b0818;
          --w-bg-soft: #140f2a;
          --w-card: #1a1533;
          --w-text: #e9e5f5;
          --w-soft: #9d96bf;
          --w-muted: #6e668e;
          --w-accent: #a89cff;
          --w-deep: #6e5fd4;
          --w-rose: #d8a5a0;
          --w-line: rgba(168, 156, 255, 0.12);
          --w-display: 'Fraunces', Georgia, serif;
          --w-body: 'Inter Tight', -apple-system, system-ui, sans-serif;
        }
        .w-landing { background: var(--w-bg); color: var(--w-text); font-family: var(--w-body); font-weight: 300; min-height: 100vh; position: relative; overflow-x: hidden; }
        .w-landing *, .w-landing *::before, .w-landing *::after { box-sizing: border-box; }
        .w-landing h1, .w-landing h2, .w-landing h3 { font-family: var(--w-display); font-weight: 300; letter-spacing: -0.02em; }
        .w-landing ::selection { background: var(--w-accent); color: var(--w-bg); }
        .w-grain {
          position: fixed; inset: 0; pointer-events: none; z-index: 2;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' seed='7'/><feColorMatrix values='0 0 0 0 0.66 0 0 0 0 0.61 0 0 0 0 1 0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
          mix-blend-mode: overlay; opacity: 0.22;
        }
        .w-container { max-width: 1200px; margin: 0 auto; padding: 0 32px; }
        .w-section { padding: 140px 0; position: relative; }
        .w-italic-accent { font-style: italic; color: var(--w-accent); font-weight: 300; }
        @media (max-width: 900px) {
          .w-section { padding: 100px 0; }
          .w-container { padding: 0 24px; }
          .w-grid-2, .w-grid-3, .w-grid-6 { grid-template-columns: 1fr !important; }
          .w-hero-title { font-size: 44px !important; }
          .w-manifesto-body { font-size: 22px !important; }
          .w-reframe-body { font-size: 22px !important; }
          .w-hero-order-visual { order: -1; }
          .w-hero-section {
            background-size: cover !important;
            background-position: 75% center !important;
            height: auto !important;
            min-height: 100vh !important;
          }
          .w-hero-content {
            padding-top: 120px !important;
            padding-bottom: 520px !important;
          }
        }
      `}</style>

      <div className="w-landing">
        <div className="w-grain" aria-hidden />

        {/* ============ NAV ============ */}
        <nav
          style={{
            position: "fixed", top: 0, left: 0, right: 0, zIndex: 30,
            background: "rgba(11, 8, 24, 0.55)",
            backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
            borderBottom: "0.5px solid var(--w-line)",
          }}
        >
          <div className="w-container" style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            height: 72,
          }}>
            <a href="/w" data-testid="w-nav-logo" aria-label="Evoluras — home" style={{ display: "inline-flex", alignItems: "center" }}>
              <EvoluraLogo size={36} variant="light" />
            </a>
            <Button
              asChild
              className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white border border-white/20 rounded-full px-6"
              data-testid="w-nav-signin"
            >
              <a href={loginHref()}>Sign In</a>
            </Button>
          </div>
        </nav>

        {/* ============ HERO ============ */}
        <section
          className="w-hero-section"
          style={{
            position: "relative",
            height: "100vh",
            minHeight: 680,
            backgroundColor: "var(--w-bg)",
            backgroundImage: `url(${HERO_IMAGE})`,
            backgroundSize: "auto 100%",
            backgroundPosition: "right center",
            backgroundRepeat: "no-repeat",
            overflow: "hidden",
          }}
        >
          {/* Left-side dark column + soft feather into image */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(90deg, var(--w-bg) 0%, var(--w-bg) 38%, rgba(11,8,24,0.7) 50%, rgba(11,8,24,0.25) 62%, transparent 75%)",
              pointerEvents: "none",
            }}
          />
          {/* Bottom fade into page bg */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: 200,
              background:
                "linear-gradient(to bottom, transparent, var(--w-bg))",
              pointerEvents: "none",
            }}
          />
          {/* Soft accent glow behind headline */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(ellipse 700px 500px at 22% 45%, rgba(168,156,255,0.18), transparent 65%)",
              pointerEvents: "none",
            }}
          />

          <div
            className="w-container w-hero-content"
            style={{
              position: "relative",
              zIndex: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              paddingTop: 72, /* clear fixed nav */
            }}
          >
            <div style={{ maxWidth: 560 }}>
              <div style={entry(120)}><Eyebrow>For the woman done performing</Eyebrow></div>
              <h1 className="w-hero-title" style={{
                fontSize: "clamp(44px, 5.2vw, 68px)", lineHeight: 1.05, margin: "22px 0 22px",
                color: "var(--w-text)", maxWidth: 520,
                textShadow: "0 2px 30px rgba(11,8,24,0.6)",
                ...entry(260),
              }}>
                Come home to{" "}
                <span className="w-italic-accent">who you actually are.</span>
              </h1>
              <p style={{
                fontSize: 17, lineHeight: 1.55, color: "var(--w-soft)", fontWeight: 300,
                maxWidth: 500, margin: "0 0 32px", ...entry(400),
              }}>
                You've spent years becoming someone other people could handle. This is a quiet place to meet yourself again — and let the people who love you help you see what's really there.
              </p>
              <div style={{ display: "flex", gap: 24, alignItems: "center", flexWrap: "wrap", ...entry(540) }}>
                <CTAButton testId="w-hero-cta">Begin — it's free</CTAButton>
                <CTAButton variant="ghost" href="#how" testId="w-hero-ghost">How it works</CTAButton>
              </div>
              <div style={{
                marginTop: 32, display: "flex", gap: 24, flexWrap: "wrap",
                ...entry(700),
              }}>
                {["No performance required", "Private by default", "Takes 12 minutes"].map((t) => (
                  <span key={t} style={{
                    display: "inline-flex", alignItems: "center", gap: 10,
                    color: "var(--w-soft)", fontSize: 12, fontWeight: 400,
                  }}>
                    <span style={{
                      width: 4, height: 4, borderRadius: "50%",
                      background: "var(--w-accent)", opacity: 0.9,
                    }} />
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ============ RECOGNITION ============ */}
        <section id="how" className="w-section">
          <div className="w-container">
            <Reveal><Eyebrow>The quiet knowing</Eyebrow></Reveal>
            <Reveal delay={100}>
              <h2 style={{ fontSize: 56, lineHeight: 1.15, margin: "28px 0 72px", maxWidth: 960, color: "var(--w-text)" }}>
                You don't need another course. You need to stop{" "}
                <span className="w-italic-accent">abandoning yourself</span>{" "}
                in small ways every day.
              </h2>
            </Reveal>

            <div className="w-grid-6" style={{
              display: "grid", gridTemplateColumns: "1fr 1fr",
              borderTop: "0.5px solid var(--w-line)",
            }}>
              {SIGNS.map((s, i) => (
                <SignRow key={i} num={String(i + 1).padStart(2, "0")} text={s} i={i} />
              ))}
            </div>

            <Reveal delay={200}>
              <p style={{
                textAlign: "center", margin: "80px auto 0", maxWidth: 720,
                fontFamily: "var(--w-display)", fontStyle: "italic", fontWeight: 300,
                fontSize: 22, lineHeight: 1.55, color: "var(--w-soft)",
              }}>
                If more than two of these landed, you're not lost.{" "}
                <span style={{ color: "var(--w-accent)" }}>You're just overdue for a conversation with yourself.</span>
              </p>
            </Reveal>
          </div>
        </section>

        {/* ============ REFRAME ============ */}
        <section className="w-section" style={{ background: "var(--w-bg-soft)" }}>
          <div className="w-container">
            <div className="w-grid-2" style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 80, alignItems: "center" }}>
              <div>
                <Reveal><Eyebrow>Why most of it hasn't worked</Eyebrow></Reveal>
                <div style={{ marginTop: 32 }}>
                  <Reveal delay={80}>
                    <p className="w-reframe-body" style={{
                      fontFamily: "var(--w-display)", fontWeight: 300, fontSize: 30,
                      letterSpacing: "-0.01em", lineHeight: 1.35, color: "var(--w-text)",
                      margin: "0 0 30px",
                    }}>
                      Authenticity isn't found in a journal alone at 5am. It's <span className="w-italic-accent">relational.</span>
                    </p>
                  </Reveal>
                  <Reveal delay={180}>
                    <p className="w-reframe-body" style={{
                      fontFamily: "var(--w-display)", fontWeight: 300, fontSize: 30,
                      letterSpacing: "-0.01em", lineHeight: 1.35, color: "var(--w-soft)",
                      margin: "0 0 30px",
                    }}>
                      The self you can't see is visible to the people who've watched you live. Your sister. Your best friend of eleven years. The colleague who's been honest with you twice, and right both times.
                    </p>
                  </Reveal>
                  <Reveal delay={280}>
                    <p className="w-reframe-body" style={{
                      fontFamily: "var(--w-display)", fontWeight: 300, fontSize: 30,
                      letterSpacing: "-0.01em", lineHeight: 1.35, color: "var(--w-text)",
                      margin: 0,
                    }}>
                      Evoluras is the structured, private way to ask them — and to <span className="w-italic-accent">finally hear it.</span>
                    </p>
                  </Reveal>
                </div>
              </div>

              {/* Stack of mirrors */}
              <div style={{ position: "relative", minHeight: 440 }}>
                <Reveal delay={200}>
                  <div style={{ position: "absolute", top: 0, left: "10%" }}>
                    <MirrorCard
                      label="Mum · on your strengths"
                      quote={`"You make people feel safe enough to tell the truth. It's not small. It's the whole thing."`}
                      tilt={-2.5} floating
                      avatarSrc={AVATARS.mum}
                    />
                  </div>
                </Reveal>
                <Reveal delay={360}>
                  <div style={{ position: "absolute", top: 140, right: 0 }}>
                    <MirrorCard
                      label="Sarah · oldest friend"
                      quote={`"You shrink yourself around him. I've wanted to say it for years."`}
                      tilt={3} floating
                      avatarSrc={AVATARS.sarah}
                    />
                  </div>
                </Reveal>
                <Reveal delay={520}>
                  <div style={{ position: "absolute", bottom: 0, left: "5%" }}>
                    <MirrorCard
                      label="Ana · your mentor"
                      quote={`"You undersell what you're actually here to do. Stop hiding behind 'I'm still figuring it out.'"`}
                      tilt={-1.5} floating
                      avatarSrc={AVATARS.ana}
                    />
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </section>

        {/* ============ TOOLS ============ */}
        <section className="w-section">
          <div className="w-container">
            <div style={{ maxWidth: 960, marginBottom: 80 }}>
              <Reveal><Eyebrow>Three small beginnings</Eyebrow></Reveal>
              <Reveal delay={100}>
                <h2 style={{ fontSize: 56, lineHeight: 1.15, margin: "28px 0 24px", color: "var(--w-text)" }}>
                  Start wherever it hurts <span className="w-italic-accent">least.</span>
                </h2>
              </Reveal>
              <Reveal delay={200}>
                <p style={{
                  fontSize: 17, lineHeight: 1.6, color: "var(--w-soft)", fontWeight: 300,
                  maxWidth: 640, margin: 0,
                }}>
                  Each takes 10–15 minutes. You can do them as a guest, with no account, no email. If something surfaces that you want to keep, then we'll talk about saving it.
                </p>
              </Reveal>
            </div>
            <div className="w-grid-3" style={{
              display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 28,
            }}>
              {TOOLS.map((t, i) => <ToolCard key={t.name} t={t} i={i} />)}
            </div>
          </div>
        </section>

        {/* ============ MANIFESTO ============ */}
        <section style={{ padding: "180px 0", position: "relative", textAlign: "center" }}>
          <div style={{
            position: "absolute", inset: 0,
            background: "radial-gradient(ellipse 700px 500px at center, rgba(168,156,255,0.12), transparent 70%)",
            pointerEvents: "none",
          }} />
          <div className="w-container" style={{ position: "relative", zIndex: 3, maxWidth: 760 }}>
            <Reveal>
              <div style={{
                fontFamily: "var(--w-display)", fontStyle: "italic",
                fontSize: 80, lineHeight: 1, color: "var(--w-accent)",
                opacity: 0.5, marginBottom: 24,
              }}>
                "
              </div>
            </Reveal>
            <Reveal delay={120}>
              <p className="w-manifesto-body" style={{
                fontFamily: "var(--w-display)", fontWeight: 300, fontSize: 30,
                lineHeight: 1.45, color: "var(--w-text)", margin: "0 0 32px",
                letterSpacing: "-0.01em",
              }}>
                The women who finally came home to themselves didn't do it by <span className="w-italic-accent">looking harder in the mirror.</span>
              </p>
            </Reveal>
            <Reveal delay={240}>
              <p className="w-manifesto-body" style={{
                fontFamily: "var(--w-display)", fontWeight: 300, fontSize: 30,
                lineHeight: 1.45, color: "var(--w-soft)", margin: 0,
                letterSpacing: "-0.01em",
              }}>
                They did it by <span className="w-italic-accent">asking the people who loved them</span> — really asking — and being brave enough to believe the answer.
              </p>
            </Reveal>
            <Reveal delay={360}>
              <div style={{
                width: 64, height: 1, background: "var(--w-accent)",
                margin: "52px auto", opacity: 0.6,
              }} />
            </Reveal>
            <Reveal delay={420}>
              <p className="w-manifesto-body" style={{
                fontFamily: "var(--w-display)", fontWeight: 300, fontSize: 26,
                lineHeight: 1.5, color: "var(--w-text)", margin: 0,
                letterSpacing: "-0.01em", fontStyle: "italic",
              }}>
                Evoluras exists for that moment when someone you trust <span style={{ color: "var(--w-accent)" }}>says the thing you needed to hear</span> but couldn't tell yourself.
              </p>
            </Reveal>
          </div>
        </section>

        {/* ============ FAQ ============ */}
        <section className="w-section" style={{ background: "var(--w-bg-soft)" }}>
          <div className="w-container">
            <div className="w-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 80 }}>
              <div>
                <Reveal><Eyebrow>Before you begin</Eyebrow></Reveal>
                <Reveal delay={100}>
                  <h2 style={{
                    fontSize: 44, lineHeight: 1.15, margin: "28px 0 0",
                    color: "var(--w-text)", maxWidth: 360,
                  }}>
                    The questions women usually <span className="w-italic-accent">ask first.</span>
                  </h2>
                </Reveal>
              </div>
              <div data-testid="w-faq-list">
                {FAQS.map((f, i) => (
                  <FaqItem
                    key={f.q}
                    q={f.q} a={f.a} index={i}
                    isOpen={openFaq === i}
                    onToggle={() => setOpenFaq(openFaq === i ? null : i)}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ============ FINAL CTA ============ */}
        <section style={{
          padding: "180px 0", textAlign: "center",
          background: "linear-gradient(180deg, var(--w-bg) 0%, var(--w-bg-soft) 100%)",
        }}>
          <div className="w-container" style={{ maxWidth: 680 }}>
            <Reveal>
              <h2 style={{ fontSize: 64, lineHeight: 1.1, margin: "0 0 32px", color: "var(--w-text)" }}>
                You can keep performing,<br />
                <span className="w-italic-accent">or you can begin.</span>
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p style={{
                fontSize: 17, lineHeight: 1.65, color: "var(--w-soft)", fontWeight: 300,
                margin: "0 auto 44px", maxWidth: 560,
              }}>
                Most women who start Evoluras tell us the same thing afterward: <em style={{ color: "var(--w-accent)", fontFamily: "var(--w-display)" }}>I didn't realise how tired I was of not being myself.</em> The door is open.
              </p>
            </Reveal>
            <Reveal delay={260}>
              <div style={{ display: "inline-flex", gap: 28, alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
                <CTAButton testId="w-final-cta">Begin — it's free</CTAButton>
                <CTAButton variant="ghost" href="/wheel" testId="w-final-ghost">Or try the Wheel of Life</CTAButton>
              </div>
            </Reveal>
            <Reveal delay={380}>
              <p style={{
                marginTop: 40, fontSize: 12, color: "var(--w-muted)", letterSpacing: "0.04em",
              }}>
                No credit card. No email required to start. No performance required, ever.
              </p>
            </Reveal>
          </div>
        </section>

        {/* ============ FOOTER ============ */}
        <footer style={{
          padding: "32px 0", borderTop: "0.5px solid var(--w-line)",
        }}>
          <div className="w-container" style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            fontSize: 12, color: "var(--w-muted)", flexWrap: "wrap", gap: 16,
          }}>
            <span>© 2026 Evoluras · Personal growth, together.</span>
            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
              <a href="/privacy" style={{ color: "var(--w-muted)", textDecoration: "none" }} data-testid="footer-privacy-link">
                Privacy
              </a>
              <a href={CTA_URL} style={{ color: "var(--w-muted)", textDecoration: "none" }}>
                evoluras.com
              </a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LandingPageW;
